import React, { useEffect, useMemo } from "react";
import cx from "classnames";

import { useDispatch, useSelector } from "react-redux";
import {
  authenticateUser,
  authenticatingUserSucceeded,
  fetchSSOUrlFromEmail,
  invalidateForgotPassword,
  logoutUser,
  signUpUser,
  ssoAuthenticationFailed,
} from "src/redux/actions/users";



import styles from "./login.module.scss";
import { RootState } from "src/redux/store";
import { useNavigate, useSearchParams } from "react-router-dom";
import _ from "lodash";
import Button from "src/components/button";
import AuthService from "src/services/auth.service";
import Reveal from "src/components/animation";
import BrowserService from "src/services/browser.service";

const SafariPopUp = '/images/popUp/safari_Popup.png';
const FirefoxPopUp = '/images/popUp/firefox_Popup.png';
const ChromePopUp = '/images/popUp/Chrome_Popup.png';
const EdgePopUp = '/images/popUp/Edge_Popup.png';
const DefaultPopUp = '/images/popUp/Default_Popup.png';

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const passwordInputRef = React.useRef<HTMLInputElement>(null);

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordText, setShowPasswordText] = React.useState(false);
  const [signUp, setSignUp] = React.useState(false);
  const [isPopupBlocked, setIsPopupBlocked] = React.useState(false);
  const [showSignupSuccess, setShowSignupSuccess] = React.useState(false);

  const redirectUrl = useMemo(() => {
    let url = searchParams.get("redirect");
    if(!url) {
      url =  "/hub";
    }

    return url;
  }, [searchParams]);

  const authenticating = useSelector(
    (state: RootState) => state.users.authentication?.authenticating,
  );

  const invalidPassword = useSelector((state: RootState) =>
    _.get(state, "users.authentication.error.error"),
  );
  const authenticated = useSelector((state: RootState) =>
    _.get(state, "users.authentication.authenticated"),
  );

  const ssoUrl = useSelector((state) => _.get(state, "users.sso.url"));
  const ssoError = useSelector((state) => _.get(state, "users.sso.error"));
  const registrationError = useSelector(
    (state: RootState) =>
      _.get(state, "users.registration.error") as {
        message: string;
      },
  );
  const signingUp = useSelector((state) =>
    _.get(state, "users.registration.loading"),
  );
  const signupSuccess = useSelector((state) =>
    _.get(state, "users.registration.success"),
  );

  useEffect(() => {
    setUsername("");
    setError({
      username: "",
      password: "",
    });

    window.addEventListener("message", handleCodeRecievedEvent, false);

    return () => {
      window.removeEventListener("message", handleCodeRecievedEvent);
    };
  }, []);

  useEffect(() => {
    if (authenticated) {
      navigate(redirectUrl);
    }
  }, [authenticated]);

  useEffect(() => {
    if (!_.isEmpty(ssoUrl)) {
      initiateSSO(ssoUrl);
    }
  }, [ssoUrl]);

  useEffect(() => {
    if (ssoError) {
      setShowPassword(true);
    }
  }, [ssoError]);

  useEffect(() => {
    if (showPassword && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [showPassword]);

  useEffect(() => {
    if (invalidPassword) {
      setError({
        ...error,
        password: invalidPassword.error_description,
      });
    }
  }, [invalidPassword]);

  useEffect(() => {
    if (registrationError) {
      setError({
        ...error,
        username: registrationError?.message,
      });
    }
  }, [registrationError]);

  useEffect(() => {
    if (!signingUp && signupSuccess) {
      setShowSignupSuccess(true);
    }
  }, [signingUp, signupSuccess])

  const handleSubmit = (event) => {
    event.preventDefault();
    const userNameError = validateUserName();

    if (_.isEmpty(error.username) && !userNameError) {
      if (showPassword) {
        dispatch(authenticateUser({ username, password }));
      } else if (signUp) {
        dispatch(signUpUser({ email: username }));
      } else {
        dispatch(fetchSSOUrlFromEmail({ email: username }));
      }
    }
  };

  const handleUserNameChange = (value: string) => {
    setError({
      ...error,
      username: "",
    });
    setUsername(value);
  };

  const handlePasswordChange = (value: string) => {
    setError({
      ...error,
      password: "",
    });
    setPassword(value.trim());
  };

  const validateUserName = () => {
    if (!username || username.trim() == "") {
      setError({
        ...error,
        username: "Enter a valid email id",
      });

      return true;
    }

    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!regex.test(username)) {
      setError({
        ...error,
        username: "Enter a valid email id",
      });

      return true;
    }

    return false;
  };

  const initiateSSO = (url) => {
    const dualScreenLeft =
      window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop =
      window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
        ? document.documentElement.clientWidth
        : window.screen.width;

    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
        ? document.documentElement.clientHeight
        : window.screen.height;

    const w = 0.8 * width;
    const h = 0.8 * height;

    const left = width / 2 - w / 2 + dualScreenLeft;
    const top = height / 2 - h / 2 + dualScreenTop;

    const windowName = "userConsole";
    const newWindow = window.open(
      url,
      windowName,
      `scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`,
    );

    if (newWindow === null || typeof newWindow === "undefined") {
      console.error("Pop-up blocked or failed to open new window");
      setIsPopupBlocked(true);
    } else {
      newWindow.focus();

      window.addEventListener("message", handleCodeRecievedEvent, false);
    }

    return false;
  };

  const handleCodeRecievedEvent = (event) => {
    if (event.data.type == "SSO_AUTHENTICATION_FAILURE") {
      dispatch(ssoAuthenticationFailed(event.data));
      console.log("SSO_AUTHENTICATION_FAILED");
      navigate("/sso/failure");
    } else if (event.data.type == "SSO_AUTHENTICATION_SUCCESS") {
      console.log("SSO_AUTHENTICATION_SUCCESS");
      dispatch(authenticatingUserSucceeded(event.data.payload));
    }
  };

  const renderButtonTitle = () => {
    if (signUp) {
      return signingUp ? "Registering" : "Sign Up";
    }
    return authenticating ? "Signing In" : "Continue";
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit(event);
    }
  };

  const getPopupImage = useMemo(() => {
    const browser = BrowserService.getCurrentBrowser();
    switch (browser) {
      case 'safari':
        return SafariPopUp
      case 'firefox':
        return FirefoxPopUp
      case 'chrome':
        return ChromePopUp
      case 'edge':
        return EdgePopUp
      case 'ie':
        return EdgePopUp
      default:
        return DefaultPopUp
    }

  },[]) 

  return (
    <div className={styles.loginContainer}>
      <img
        src={'/images/Paperflite.png'}
        alt="Paperflite Logo"
        className={styles.logo} 
      />

      {isPopupBlocked && <div className={styles.popupBlocked}>
        <img src={getPopupImage} />
      </div>}

      {!showSignupSuccess && !isPopupBlocked && <> <h1 className={styles.title}>
        Hello! {signUp ? "Sign up" : "Sign in"} to explore Paperflite
      </h1>
        <form
          className={styles.loginForm}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyPress}
        >
          <Reveal show={true} type="fade" delay={2}>
            <input
              className={styles.input}
              type="text"
              placeholder={signUp ? "Enter work email" : "Enter your username"}
              value={username}
              onChange={(e) => handleUserNameChange(e.target.value)}
            />
          </Reveal>
          {error.username && (
            <p className={styles.error}>
              <i className="paperflite-danger"></i>
              <label>{error.username}</label>
            </p>
          )}
          {signUp && (
            <p className={styles.termAndConitions}>
              By registering you confirm that your agree with our&nbsp;
              <a
                href="https://app.paperflite.com/terms"
                target="_blank"
                rel="noreferrer"
              >
                Terms &amp; Conditions
              </a>
              &nbsp;and&nbsp;
              <a
                href="https://app.paperflite.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            </p>
          )}
          <Reveal show={showPassword && !signUp} type="fade" delay={2}>
            <span className={styles.passwordInput}>
              <input
                ref={passwordInputRef}
                className={styles.input}
                type={showPasswordText ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
              />
              <i
                onClick={() => setShowPasswordText(!showPasswordText)}
                className={cx(
                  showPasswordText ? "paperflite-hidden-eye" : "paperflite-eye",
                )}
              ></i>
            </span>
          </Reveal>
          <div className={styles.forgotPasswordSection}>
            <p
              className={cx(
                _.isEmpty(error.password) ? styles.hideError : styles.error,
              )}
            >
              <i className="paperflite-danger"></i>
              <label>{error.password}</label>
            </p>
            {showPassword && !signUp && (
              <span
                className={styles.forgotPassword}
                onClick={() => {
                  dispatch(invalidateForgotPassword());
                  navigate("/forgot-password")}
                }
              >
                Forgot Password?
              </span>
            )}
          </div>
          <span onClick={handleSubmit}>
            <Button
              className={styles.button}
              variant="primary"
              icon="paperflite-right-arrow"
              disabled={authenticating || signingUp}
            >
              {renderButtonTitle()}
            </Button>
          </span>
        </form>
        {!signUp && (
          <p className={styles.signUp} onClick={() => setSignUp(true)}>
            Don&apos;t have an account?
            <span className={styles.signUpText}>Sign up</span>
          </p>
        )}</>}

        {showSignupSuccess && <div className={styles.signUpSuccessMessage}>
          <h1 className={styles.title}>
            Strangers, No more!
          </h1>
          <p>
            Thanks for joining Paperflite!
          </p>
          <p>
            One of our customer success representatives will be in touch with you shortly.
          </p>
          <p>
            Please watch your mailbox for an email with next steps.
          </p>
        </div>}

        {!isPopupBlocked && signUp && (
          <p className={styles.signUp} onClick={() => {
            setSignUp(false)
            setShowSignupSuccess(false);
            setUsername("")
            }}>
            Already using Paperflite?
            <span className={styles.signUpText}>Sign in</span>
          </p>
        )}

    </div>
  );
}

export default LoginPage;
